"use client";

import { useState, useTransition } from "react";

import { MailWarningIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { resendVerificationAction } from "@/features/auth/actions";
import { useCurrentUser } from "@/features/auth/components/current-user-provider";

/** Shown in the app shell until the user verifies their email. */
export function VerifyEmailBanner() {
  const t = useTranslations("auth.verify");
  const tc = useTranslations("common");
  const user = useCurrentUser();
  const [sent, setSent] = useState(false);
  const [isPending, startTransition] = useTransition();

  if (user.emailVerified) return null;

  return (
    <Alert>
      <MailWarningIcon aria-hidden />
      <AlertTitle>{t("bannerTitle")}</AlertTitle>
      <AlertDescription className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span>
          {t.rich("bannerSentTo", {
            email: user.email,
            strong: (chunks) => <strong className="font-medium">{chunks}</strong>,
          })}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={isPending || sent}
          onClick={() =>
            startTransition(async () => {
              const result = await resendVerificationAction();
              if (result.ok) {
                setSent(true);
                toast.success(t("resentToast"));
              } else {
                toast.error(result.error);
              }
            })
          }
        >
          {sent ? t("resent") : isPending ? tc("sending") : t("resend")}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
